import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { editDistributor, getDistributorsList } from '../../redux-store/actions/userManagement';
import { AddDistributorRequestBody, DistributorListType } from '../../redux-store/types/api-types';
import { useHistory } from 'react-router';
import { Location } from 'history';
import { ColumnType } from '../DataTable';
import { DistributorTypesForUserManageMent } from '../../utils/constant';
import { getDistributorsListForFilters } from '../../redux-store/actions';
import UserMgmtListPage, {
  NameLinkCell,
  buildAccessColumn,
  buildActionsColumn,
  matchesUserSearch,
} from './UserMgmtListPage';

function Distributors({
  location,
}: {
  location: Location<{ distributorType: string }>;
}): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [distributorType, setDistributerType] = useState(
    location.state?.distributorType ? location.state.distributorType : 'company'
  );

  const isCompany = distributorType === 'company';

  return (
    <UserMgmtListPage<DistributorListType>
      key={distributorType}
      loadDeps={[distributorType]}
      loadData={async () => {
        if (distributorType === DistributorTypesForUserManageMent.Individual) {
          return (await dispatch(
            getDistributorsList({ type: distributorType })
          )) as unknown as DistributorListType[];
        }
        return (await dispatch(
          getDistributorsListForFilters({ type: distributorType })
        )) as unknown as DistributorListType[];
      }}
      searchPlaceholder="Search for Distributors"
      inviteLabel="Add Distributor"
      onInvite={() => history.push('add-distributor', { typeOfDistributor: distributorType })}
      toolbarExtra={
        <Select
          size="small"
          value={distributorType}
          onChange={(event: SelectChangeEvent) => setDistributerType(event.target.value)}
          sx={{
            minWidth: 140,
            borderRadius: '8px',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
          }}>
          {Object.keys(DistributorTypesForUserManageMent).map((item) => (
            <MenuItem
              value={DistributorTypesForUserManageMent[item]}
              key={DistributorTypesForUserManageMent[item]}
              sx={{ fontFamily: 'Inter, sans-serif' }}>
              {item}
            </MenuItem>
          ))}
        </Select>
      }
      filterRow={(row, q) =>
        matchesUserSearch(q, [row.name, row.user?.email, row.panNumber, row.arnCode, row.rm?.name])
      }
      getColumns={({ onToggleAccess }) => {
        const companyColumns: ColumnType[] = [
          {
            header: 'Distributor Name',
            align: 'left',
            headerAlign: 'left',
            renderCell: (row: DistributorListType) => (
              <NameLinkCell
                label={row.name || 'N/A'}
                to={{ pathname: 'distributor-details', state: { distributorId: row.id } }}
              />
            ),
          },
          {
            header: 'Company PAN',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: DistributorListType) => row.panNumber || 'N/A',
          },
          {
            header: 'ARN Code',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: DistributorListType) => row.arnCode || 'N/A',
          },
          buildAccessColumn(onToggleAccess),
          buildActionsColumn((row: DistributorListType) => [
            {
              label: 'View Details',
              icon: '/images/view-application.svg',
              to: { pathname: 'distributor-details', state: { distributorId: row.id } },
            },
            {
              label: 'Mapped Users',
              icon: '/images/srm.svg',
              to: { pathname: 'distributor', state: { distributorId: row.id } },
            },
          ]),
        ];

        const individualColumns: ColumnType[] = [
          {
            header: 'Distributor Name',
            align: 'left',
            headerAlign: 'left',
            renderCell: (row: DistributorListType) => (
              <NameLinkCell
                label={row.name || 'N/A'}
                to={{ pathname: 'distributor-details', state: { distributorId: row.id } }}
              />
            ),
          },
          {
            header: 'Email Id',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: DistributorListType) => row.user?.email || 'N/A',
          },
          {
            header: 'PAN',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: DistributorListType) => row.panNumber || 'N/A',
          },
          {
            header: 'ARN Code',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: DistributorListType) => row.arnCode || 'N/A',
          },
          {
            header: 'RM Name',
            align: 'left',
            headerAlign: 'left',
            valueGetter: (row: DistributorListType) => row.rm?.name || 'N/A',
          },
          buildAccessColumn(onToggleAccess),
          buildActionsColumn((row: DistributorListType) => [
            {
              label: 'View Details',
              icon: '/images/view-application.svg',
              to: { pathname: 'distributor-details', state: { distributorId: row.id } },
            },
          ]),
        ];

        return isCompany ? companyColumns : individualColumns;
      }}
      onAccessChange={async (row, nextActive) =>
        (await dispatch(
          editDistributor(row.id as number, {
            ...(row as unknown as AddDistributorRequestBody),
            isActive: nextActive,
          })
        )) as unknown as DistributorListType
      }
    />
  );
}

export default Distributors;
